import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getDadosCliente, registrarCompensacao, adicionarDoacao, loadData, resetClienteData } from "@/services/mockDB";
import { calcularCashback, DEFAULT_CASHBACK_RULES } from "@/utils/cashbackCalculator";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, EyeOff, Sprout, Wallet, LogOut, Plane, Globe, Cloud, TreePine, CheckCircle, Clock, User, FileText, Lock, CreditCard, Heart, RotateCcw } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";

const ClientDashboard: React.FC = () => {
  const navigate = useNavigate();
  const CLIENT_ID = '1'; // ID do cliente João Silva

  const [showCardNumber, setShowCardNumber] = useState(false);
  const [mudasRegion, setMudasRegion] = useState("");
  const [mudasProject, setMudasProject] = useState("");
  const [socialRegion, setSocialRegion] = useState("");
  const [socialProject, setSocialProject] = useState("");
  const [destinacaoConfirmada, setDestinacaoConfirmada] = useState(false);
  const [filtroExtrato, setFiltroExtrato] = useState<"todos" | "entradas" | "saidas">("todos");
  const [bloqueioTemporario, setBloqueioTemporario] = useState(false);
  
  // Estados reativos para gerenciamento de saldo e investimentos (sincronizados com mockDB)
  const [saldoLivre, setSaldoLivre] = useState(0.00);
  const [totalInvestidoMudas, setTotalInvestidoMudas] = useState(0.00);
  const [transacoes, setTransacoes] = useState<Array<{
    dataHora: string;
    origem: string;
    tipo: string;
    destino: string;
    valor: number;
    tipoTransacao: "entrada" | "saida";
  }>>([]);
  
  // Estados para doação livre
  const [valorDoacao, setValorDoacao] = useState("");
  const [categoriaDoacao, setCategoriaDoacao] = useState("");
  const [regiaoDoacao, setRegiaoDoacao] = useState("");
  const [projetoDoacao, setProjetoDoacao] = useState("");

  // Estado para resumo acumulativo de destinações
  const [resumoDestinacoes, setResumoDestinacoes] = useState<Array<{
    id: string;
    tipo: 'mudas' | 'social';
    valor: number;
    regiao: string;
    projeto: string;
  }>>([]);

  // Carrega dados iniciais do mockDB
  useEffect(() => {
    const loadClienteData = () => {
      const dados = getDadosCliente(CLIENT_ID);
      if (dados) {
        setSaldoLivre(dados.saldoLivre);
        setTotalInvestidoMudas(dados.totalInvestidoMudas);
        setTransacoes(dados.transacoes);
        setDestinacaoConfirmada(dados.destinacaoConfirmada);
        setResumoDestinacoes(dados.resumoDestinacoes);
      }
    };

    // Carrega dados iniciais
    loadClienteData();

    // Listener para atualizações do localStorage (de outras abas/componentes)
    const handleStorageUpdate = () => {
      loadClienteData();
    };

    window.addEventListener('mockDB:updated', handleStorageUpdate);
    window.addEventListener('storage', handleStorageUpdate);

    return () => {
      window.removeEventListener('mockDB:updated', handleStorageUpdate);
      window.removeEventListener('storage', handleStorageUpdate);
    };
  }, []);

  // 1. Defina o Voo Padrão
  const VOO_PADRAO = { valor: 500.00, rota: 'SDU-GRU' };

  // 2. Calcule os valores iniciais baseados na Tabela (usa DEFAULT_CASHBACK_RULES)
  // A função calcularCashback agora usa split fixo: 17.5% mudas, 17.5% social, 65% carteira
  const valoresIniciais = calcularCashback(VOO_PADRAO.valor, DEFAULT_CASHBACK_RULES, 6.00);
  
  // 4. Inicialize os valores com os calculados (e não com valores fixos)
  const cashbackTotal = valoresIniciais.financeiro.total;
  const mudasValue = valoresIniciais.financeiro.mudas;
  const socialValue = valoresIniciais.financeiro.social;
  const saldoCarteiraInicial = valoresIniciais.financeiro.carteira;
  
  // Cálculos dinâmicos baseados no estado
  const valorNecessarioMuda = 6.00;
  const faltamMudas = Math.max(0, valorNecessarioMuda - totalInvestidoMudas);
  const mudasProgress = totalInvestidoMudas >= valorNecessarioMuda 
    ? 100 
    : Math.round((totalInvestidoMudas / valorNecessarioMuda) * 100);
  const mudasCompletas = Math.floor(totalInvestidoMudas / valorNecessarioMuda);

  const canConfirm = mudasRegion && mudasProject && socialRegion && socialProject;

  // Lógica "Burra" (Crash-proof) para pegar a lista de projetos
  const getProjetosMudasPorRegiao = (regiao: string): string[] => {
    if (!regiao) return [];
    if (regiao === "sudeste") return ["Reflorestamento Serra do Mar (SP/RJ)", "Mata Atlântica Viva (MG)"];
    if (regiao === "nordeste") return ["Recuperação Caatinga (CE/PE)", "Manguezais do Recôncavo (BA)"];
    if (regiao === "norte") return ["Guardiões da Amazônia (AM)", "Proteção Floresta Amazônica (PA)"];
    if (regiao === "sul") return ["Araucárias do Futuro (PR)", "Mata Atlântica Sul (SC)"];
    if (regiao === "centro-oeste") return ["Nascentes do Pantanal (MT)", "Conservação do Cerrado (GO)"];
    return []; // Retorna lista vazia se não tiver região, evita crash
  };

  const getProjetosSociaisPorRegiao = (regiao: string): string[] => {
    if (!regiao) return [];
    if (regiao === "sudeste") return ["Água para Todos - Cisternas (SP)", "Educação Rural (RJ)", "Saúde Básica (MG)"];
    if (regiao === "nordeste") return ["Água para Todos - Cisternas (Sertão)", "Educação Rural (Nordeste)", "Saúde Básica (Nordeste)"];
    if (regiao === "norte") return ["Água para Comunidades (AM)", "Educação Indígena (PA)", "Saúde na Floresta (AC)"];
    if (regiao === "sul") return ["Água Limpa (PR)", "Educação no Campo (SC)", "Saúde Comunitária (RS)"];
    if (regiao === "centro-oeste") return ["Água para Todos (MT)", "Educação Rural (GO)", "Saúde Básica (MS)"];
    return []; // Retorna lista vazia se não tiver região, evita crash
  };


  // Dados seguros para projetos de Mudas por região
  const PROJETOS_MUDAS: Record<string, Array<{ id: string; nome: string }>> = {
    "sudeste": [
      { id: "serra-mar", nome: "Reflorestamento Serra do Mar (SP/RJ)" },
      { id: "mata-atlantica", nome: "Mata Atlântica Viva (MG)" }
    ],
    "nordeste": [
      { id: "caatinga", nome: "Recuperação Caatinga (CE/PE)" },
      { id: "manguezais", nome: "Manguezais do Recôncavo (BA)" }
    ],
    "norte": [
      { id: "amazonia", nome: "Guardiões da Amazônia (AM)" },
      { id: "floresta-amazonica", nome: "Proteção Floresta Amazônica (PA)" }
    ],
    "sul": [
      { id: "araucarias", nome: "Araucárias do Futuro (PR)" },
      { id: "mata-atlantica-sul", nome: "Mata Atlântica Sul (SC)" }
    ],
    "centro-oeste": [
      { id: "pantanal", nome: "Nascentes do Pantanal (MT)" },
      { id: "cerrado", nome: "Conservação do Cerrado (GO)" }
    ]
  };

  // Dados seguros para projetos sociais por região
  const PROJETOS_SOCIAIS: Record<string, Array<{ id: string; nome: string }>> = {
    "sudeste": [
      { id: "agua-sp", nome: "Água para Todos - Cisternas (SP)" },
      { id: "educacao-rj", nome: "Educação Rural (RJ)" },
      { id: "saude-mg", nome: "Saúde Básica (MG)" }
    ],
    "nordeste": [
      { id: "agua", nome: "Água para Todos - Cisternas (Sertão)" },
      { id: "educacao", nome: "Educação Rural (Nordeste)" },
      { id: "saude", nome: "Saúde Básica (Nordeste)" }
    ],
    "norte": [
      { id: "agua-am", nome: "Água para Comunidades (AM)" },
      { id: "educacao-pa", nome: "Educação Indígena (PA)" },
      { id: "saude-ac", nome: "Saúde na Floresta (AC)" }
    ],
    "sul": [
      { id: "agua-pr", nome: "Água Limpa (PR)" },
      { id: "educacao-sc", nome: "Educação no Campo (SC)" },
      { id: "saude-rs", nome: "Saúde Comunitária (RS)" }
    ],
    "centro-oeste": [
      { id: "agua-mt", nome: "Água para Todos (MT)" },
      { id: "educacao-go", nome: "Educação Rural (GO)" },
      { id: "saude-ms", nome: "Saúde Básica (MS)" }
    ]
  };

  // Mapear valores para nomes legíveis
  const regioesMap: { [key: string]: string } = {
    "sudeste": "Sudeste",
    "nordeste": "Nordeste",
    "sul": "Sul",
    "norte": "Norte",
    "centro-oeste": "Centro-Oeste"
  };

  const projetosMudasMap: { [key: string]: string } = {
    "serra-mar": "Reflorestamento Serra do Mar",
    "mata-atlantica": "Mata Atlântica Viva",
    "caatinga": "Recuperação Caatinga",
    "manguezais": "Manguezais do Recôncavo",
    "amazonia": "Guardiões da Amazônia",
    "floresta-amazonica": "Proteção Floresta Amazônica",
    "araucarias": "Araucárias do Futuro",
    "mata-atlantica-sul": "Mata Atlântica Sul",
    "pantanal": "Nascentes do Pantanal",
    "cerrado": "Conservação do Cerrado"
  };

  const projetosSociaisMap: { [key: string]: string } = {
    "agua": "Água para Todos (Cisternas)",
    "agua-sp": "Água para Todos - Cisternas (SP)",
    "agua-am": "Água para Comunidades (AM)",
    "agua-pr": "Água Limpa (PR)",
    "agua-mt": "Água para Todos (MT)",
    "educacao": "Educação Rural",
    "educacao-rj": "Educação Rural (RJ)",
    "educacao-pa": "Educação Indígena (PA)",
    "educacao-sc": "Educação no Campo (SC)",
    "educacao-go": "Educação Rural (GO)",
    "saude": "Saúde Básica",
    "saude-mg": "Saúde Básica (MG)",
    "saude-ac": "Saúde na Floresta (AC)",
    "saude-rs": "Saúde Comunitária (RS)",
    "saude-ms": "Saúde Básica (MS)"
  };

  // Obter projetos disponíveis para doação livre baseado na categoria e região (defensivo - sem useMemo)
  const projetosDisponiveisDoacao = (() => {
    if (!regiaoDoacao || !categoriaDoacao) return [];
    
    try {
      if (categoriaDoacao === "Apoio a Mudas") {
        const projetos = getProjetosMudasPorRegiao(regiaoDoacao);
        return projetos.map((nome, index) => ({ id: `mudas-${index}`, nome }));
      } else if (categoriaDoacao === "Projetos Sociais") {
        const projetos = getProjetosSociaisPorRegiao(regiaoDoacao);
        return projetos.map((nome, index) => ({ id: `social-${index}`, nome }));
      }
    } catch (error) {
      console.error("Erro ao carregar projetos:", error);
      return [];
    }
    
    return [];
  })();

  // Função unificada para doações
  const handleDonation = (valor: number, tipo: string, destino: string) => {
    if (valor > saldoLivre) {
      alert("Saldo insuficiente!");
      return;
    }

    // Adiciona doação no mockDB
    adicionarDoacao(CLIENT_ID, valor, tipo, destino);

    // Recarrega dados do mockDB
    const dados = getDadosCliente(CLIENT_ID);
    if (dados) {
      setSaldoLivre(dados.saldoLivre);
      setTotalInvestidoMudas(dados.totalInvestidoMudas);
      setTransacoes(dados.transacoes);
      setResumoDestinacoes(dados.resumoDestinacoes);
    }

    // Limpar campos de doação livre
    setValorDoacao("");
    setCategoriaDoacao("");
    setRegiaoDoacao("");
    setProjetoDoacao("");
  };

  const handleConfirm = () => {
    // Usar valores diretamente dos selects (já são os nomes completos)
    const regiaoMudas = mudasRegion || "";
    const projetoMudas = mudasProject || "";
    const regiaoSocial = socialRegion || "";
    const projetoSocial = socialProject || "";

    // Registra compensação no mockDB
    registrarCompensacao(
      CLIENT_ID,
      mudasValue,
      socialValue,
      regiaoMudas,
      projetoMudas,
      regiaoSocial,
      projetoSocial
    );

    // Recarrega dados do mockDB
    const dados = getDadosCliente(CLIENT_ID);
    if (dados) {
      setSaldoLivre(dados.saldoLivre);
      setTotalInvestidoMudas(dados.totalInvestidoMudas);
      setTransacoes(dados.transacoes);
      setDestinacaoConfirmada(dados.destinacaoConfirmada);
      setResumoDestinacoes(dados.resumoDestinacoes);
    }

    alert("Destinação confirmada! R$ 10,00 foram liberados no seu cartão.");
  };

  const handleCompletarMuda = () => {
    if (faltamMudas > saldoLivre) {
      alert(`Saldo insuficiente. Você precisa de R$ ${faltamMudas.toFixed(2).replace(".", ",")} mas tem apenas R$ ${saldoLivre.toFixed(2).replace(".", ",")}.`);
      return;
    }
    
    // Usar valores diretamente dos selects (já são os nomes completos)
    const regiaoMudas = mudasRegion || "";
    const projetoMudas = mudasProject || "";
    
    handleDonation(faltamMudas, "Apoio a Mudas", `${regiaoMudas} - ${projetoMudas}`);
    alert(`🌲 Parabéns! Você completou 1 muda plantada!`);
  };

  const handleDoacaoLivre = () => {
    const valor = parseFloat(valorDoacao.replace(",", "."));
    
    if (!valor || valor <= 0) {
      alert("Por favor, insira um valor válido.");
      return;
    }

    if (valor > saldoLivre) {
      alert("Saldo insuficiente!");
      return;
    }

    if (!categoriaDoacao || !regiaoDoacao || !projetoDoacao) {
      alert("Por favor, preencha todos os campos.");
      return;
    }

    // Usar valores diretamente (defensivo)
    const nomeProjeto = projetoDoacao || "";
    const regiaoNome = regiaoDoacao || "";
    const destinoCompleto = `${categoriaDoacao} - ${regiaoNome} - ${nomeProjeto}`;

    handleDonation(valor, categoriaDoacao, destinoCompleto);
    alert(`Doação de R$ ${valor.toFixed(2).replace(".", ",")} realizada com sucesso!`);
  };

  const viagens = [
    {
      data: "20/12/2025",
      hora: "14:30 - 16:15",
      rota: "SDU - GRU",
      tipoAeronave: "Airbus A320",
      assentos: 180,
      distancia: "366 km",
      valorPassagem: 500.00,
      emissao: 60,
      safPercentual: "25%",
      cashbackTotal: 20.00,
      taxaCashback: 4,
      status: destinacaoConfirmada ? "Compensado" : "Pendente"
    }
  ];

  // Filtrar transações baseado na aba selecionada
  let transacoesFiltradas = transacoes;
  if (filtroExtrato === "entradas") {
    transacoesFiltradas = transacoes.filter(t => t.tipoTransacao === "entrada");
  } else if (filtroExtrato === "saidas") {
    transacoesFiltradas = transacoes.filter(t => t.tipoTransacao === "saida");
  }

  // Função para resetar dados de demonstração
  const handleResetDemo = () => {
    if (confirm("ATENÇÃO: Isso vai apagar todas as doações e resetar o saldo para o estado inicial. Deseja continuar?")) {
      // 1. Reseta os dados do cliente no mockDB
      resetClienteData(CLIENT_ID);
      
      // 2. Limpa qualquer chave adicional do localStorage (se houver)
      localStorage.removeItem('banclima_transactions_client_1');
      
      // 3. Dispara evento para avisar a aba do Admin (se estiver aberta)
      window.dispatchEvent(new Event("storage"));
      window.dispatchEvent(new Event("mockDB:updated"));
      
      // 4. Mostra feedback
      alert("Dados resetados com sucesso! A página será recarregada.");
      
      // 5. Recarrega a página atual
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Olá, João! 🌱</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetDemo}
                className="bg-red-50 hover:bg-red-100 text-red-700 border-red-200 hover:border-red-300"
                title="Usar apenas para testes - Resetar dados de demonstração"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Resetar Demo
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/client/login")}
                className="text-gray-600 hover:text-gray-900"
                title="Sair"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Botão Meus Dados */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate("/client/profile")}
            className="flex items-center gap-2"
          >
            <User className="h-4 w-4" />
            Meus Dados
          </Button>
        </div>

        {/* Grid de Métricas de Impacto */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Card 1: Viagens */}
          <Card className="shadow-md h-28">
            <CardContent className="p-4 h-full flex flex-row items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase mb-0.5">Total de Viagens</p>
                <p className="text-2xl font-bold text-gray-800">1</p>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                <Plane className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>

          {/* Card 2: Distância */}
          <Card className="shadow-md h-28">
            <CardContent className="p-4 h-full flex flex-row items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase mb-0.5">Distância (km)</p>
                <p className="text-2xl font-bold text-gray-800">366</p>
              </div>
              <div className="p-2 bg-purple-50 rounded-lg text-purple-600">
                <Globe className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>

          {/* Card 3: Emissões */}
          <Card className="shadow-md h-28">
            <CardContent className="p-4 h-full flex flex-row items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase mb-0.5 whitespace-nowrap tracking-tight">
                  <span className="hidden sm:inline">Emissões Compensadas (kgCO₂e)</span>
                  <span className="sm:hidden">Emissões Comp. (kgCO₂e)</span>
                </p>
                <p className="text-2xl font-bold text-gray-800">{(viagens[0]?.emissao ? (viagens[0].emissao / 1000).toFixed(2).replace(".", ",") : "0,00")} t</p>
              </div>
              <div className="p-2 bg-gray-50 rounded-lg text-gray-600">
                <Cloud className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>

          {/* Card 4: Mudas */}
          <Card className="shadow-md h-28">
            <CardContent className={`p-4 h-full ${totalInvestidoMudas > 0 && mudasProgress < 100 ? 'flex flex-col' : 'flex flex-row items-center justify-between'}`}>
              {totalInvestidoMudas > 0 && mudasProgress < 100 ? (
                <>
                  {/* Topo: Título, Valor e Ícone na mesma linha */}
                  <div className="flex flex-row items-center justify-between mb-2">
                    <div>
                      <p className="text-xs text-gray-500 uppercase mb-0.5">Mudas Plantadas</p>
                      <p className="text-2xl font-bold text-gray-800">{mudasCompletas}</p>
                    </div>
                    <div className="p-2 bg-green-50 rounded-lg text-green-600">
                      <TreePine className="w-5 h-5" />
                    </div>
                  </div>
                  {/* Rodapé: Barra e Info Financeira */}
                  <div className="mt-auto">
                    <div className="w-full bg-gray-100 rounded-full h-1.5 mb-1">
                      <div 
                        className="bg-green-500 h-1.5 rounded-full" 
                        style={{ width: `${mudasProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-[10px] text-gray-400">
                      Falta <span className="font-bold text-green-600">R$ {faltamMudas.toFixed(2).replace(".", ",")}</span> para completar a compra de 1 muda
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-0.5">Mudas Plantadas</p>
                    <p className="text-2xl font-bold text-gray-800">{mudasCompletas}</p>
                  </div>
                  <div className="p-2 bg-green-50 rounded-lg text-green-600">
                    <TreePine className="w-5 h-5" />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tabela de Viagens - Logo abaixo dos cards */}
        <Card className="shadow-sm border border-gray-100 rounded-xl bg-white mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Minhas Viagens Aéreas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="table-auto w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Data</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Hora</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Rota / Voo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Tipo Aeronave</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Assentos</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Distância</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Valor Passagem</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Emissões KgCO₂e</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Total de Cashback</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {viagens.map((viagem, index) => (
                    <tr key={index} className="hover:bg-gray-50 border-b border-gray-100">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {viagem.data}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {viagem.hora}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {viagem.rota}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {viagem.tipoAeronave}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {viagem.assentos}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {viagem.distancia}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        R$ {viagem.valorPassagem.toFixed(2).replace(".", ",")}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <div className="space-y-0.5">
                          <span className="text-[#0e7cb4] font-medium">{viagem.emissao}</span>
                          {viagem.safPercentual && (
                            <div className="text-xs text-gray-500">com SAF {viagem.safPercentual}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        R$ {viagem.cashbackTotal.toFixed(2).replace(".", ",")} ({viagem.taxaCashback}%)
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Extrato de Transações */}
        <Card className="shadow-sm border border-gray-100 rounded-xl bg-white mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Extrato de Transações</CardTitle>
            {/* Abas de Filtro */}
            <div className="flex gap-2 mt-4">
              <Button
                variant={filtroExtrato === "todos" ? "default" : "outline"}
                size="sm"
                onClick={() => setFiltroExtrato("todos")}
                className="text-sm"
              >
                Todos
              </Button>
              <Button
                variant={filtroExtrato === "entradas" ? "default" : "outline"}
                size="sm"
                onClick={() => setFiltroExtrato("entradas")}
                className="text-sm"
              >
                Entradas (Ganhos)
              </Button>
              <Button
                variant={filtroExtrato === "saidas" ? "default" : "outline"}
                size="sm"
                onClick={() => setFiltroExtrato("saidas")}
                className="text-sm"
              >
                Saídas (Destinações)
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="table-auto w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Data/Hora</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Origem</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Destino</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Valor</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {transacoesFiltradas.length > 0 ? (
                    transacoesFiltradas.map((transacao, index) => (
                      <tr key={index} className="hover:bg-gray-50 border-b border-gray-100">
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transacao.dataHora}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                          {transacao.origem}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                            transacao.tipoTransacao === "entrada"
                              ? "bg-green-100 text-green-800"
                              : "bg-orange-100 text-orange-800"
                          }`}>
                            {transacao.tipo}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                          {transacao.destino}
                        </td>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm font-semibold ${
                          transacao.tipoTransacao === "entrada" ? "text-green-600" : "text-orange-600"
                        }`}>
                          {transacao.tipoTransacao === "entrada" ? "+" : "-"} R$ {transacao.valor.toFixed(2).replace(".", ",")}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center justify-center space-y-3">
                          <FileText className="h-12 w-12 text-gray-300" />
                          <div className="text-center">
                            <p className="text-sm font-medium text-gray-600">
                              {filtroExtrato === "entradas" 
                                ? "Nenhuma entrada registrada."
                                : filtroExtrato === "saidas"
                                ? "Nenhuma destinação realizada ainda."
                                : "Nenhuma transação encontrada."}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {filtroExtrato === "saidas" 
                                ? "Após confirmar a destinação do seu cashback, as transações aparecerão aqui."
                                : ""}
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Área Principal: Grid 2 Colunas */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          {/* Coluna da Esquerda: A Carteira (1/3) */}
          <div className="lg:col-span-1">
            <Card className="shadow-md h-full flex flex-col">
              <CardHeader>
                <CardTitle className="text-lg">Carteira</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 flex-1 flex flex-col justify-between">
                {/* Cartão Virtual */}
                <div className="relative rounded-xl overflow-hidden">
                  <img
                    src="/images/cartão moeda.png"
                    alt="Cartão Moeda Social Carbono"
                    className="w-full"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (target.src.includes('cartão moeda')) {
                        target.src = "/images/cartão-moeda.png";
                      } else if (target.src.includes('cartão-moeda')) {
                        target.src = "/images/cartãomoeda.png";
                      }
                    }}
                  />
                  <div className="absolute bottom-2 left-6 text-white font-mono text-xl tracking-wider drop-shadow-md z-10">
                    {showCardNumber ? "•••• •••• •••• 1234" : "•••• •••• •••• ••••"}
                  </div>
                </div>
                
                {/* Saldo Livre */}
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-1">Saldo Livre</p>
                  {destinacaoConfirmada || saldoLivre > 0 ? (
                    <p className="text-4xl font-bold text-green-600">
                      R$ {saldoLivre.toFixed(2).replace(".", ",")}
                    </p>
                  ) : (
                    <>
                      <p className="text-4xl font-bold text-gray-400">
                        R$ 0,00
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Confirme a destinação para liberar seu saldo
                      </p>
                    </>
                  )}
                </div>

                {/* Bloqueio Temporário */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">Bloqueio Temporário</span>
                  </div>
                  <Switch
                    checked={bloqueioTemporario}
                    onCheckedChange={setBloqueioTemporario}
                  />
                </div>

                {/* Ações Rápidas */}
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setShowCardNumber(!showCardNumber)}
                  >
                    {showCardNumber ? (
                      <>
                        <EyeOff className="h-4 w-4 mr-2" />
                        Ocultar Número
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4 mr-2" />
                        Ver Número do Cartão
                      </>
                    )}
                  </Button>
                  
                  {/* Grid de Ações Secundárias */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs h-9"
                      onClick={() => alert("Funcionalidade em desenvolvimento")}
                    >
                      <Lock className="h-3 w-3 mr-1" />
                      Ver Senha
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs h-9"
                      onClick={() => alert("Solicitação de 2ª via enviada")}
                    >
                      <CreditCard className="h-3 w-3 mr-1" />
                      2ª Via
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Coluna da Direita: Centro de Ação e Histórico (2/3) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Renderização Condicional: Card de Pendência OU Card de Sucesso */}
            {!destinacaoConfirmada ? (
              /* Card de Ação Necessária (Antes da Confirmação) */
              <Card className="shadow-lg border-2 border-yellow-400 bg-yellow-50/30">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <span>⚠️</span>
                    Pendência: Voo SDU-GRU (Restam 3 dias)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-700">
                    Você tem <span className="font-bold text-gray-900">R$ {cashbackTotal.toFixed(2).replace(".", ",")}</span> de cashback. Destine agora para liberar seu saldo.
                  </p>

                  {/* Interface de Decisão */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200 space-y-4">
                    {/* Lado A: Mudas */}
                    <div className="space-y-3 pb-4 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        <Sprout className="h-4 w-4 text-green-600" />
                        <span className="font-semibold text-sm">Apoio a Mudas</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-baseline gap-2">
                          <span className="text-xl font-bold text-green-600">R$ {mudasValue.toFixed(2).replace(".", ",")}</span>
                          <span className="text-sm font-medium text-green-600/70">(17,5%)</span>
                        </div>
                        <div className="mb-2">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-600">Progresso</span>
                            <span className="text-xs text-gray-600">{mudasProgress}%</span>
                          </div>
                          <Progress value={mudasProgress} className="h-2" />
                          <p className="text-xs text-gray-600 mt-1">
                            Falta R$ {faltamMudas.toFixed(2).replace(".", ",")} para 1 árvore inteira
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label htmlFor="mudas-region" className="text-xs">Região</Label>
                            <select
                              id="mudas-region"
                              className="w-full border border-gray-300 rounded-md p-2 bg-white text-gray-900 text-sm h-9"
                              value={mudasRegion}
                              onChange={(e) => {
                                setMudasRegion(e.target.value);
                                setMudasProject("");
                              }}
                            >
                              <option value="">Selecione...</option>
                              <option value="sudeste">Sudeste</option>
                              <option value="nordeste">Nordeste</option>
                              <option value="sul">Sul</option>
                              <option value="norte">Norte</option>
                              <option value="centro-oeste">Centro-Oeste</option>
                            </select>
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="mudas-project" className="text-xs">Projeto</Label>
                            <select
                              id="mudas-project"
                              className="w-full border border-gray-300 rounded-md p-2 bg-white text-gray-900 text-sm h-9 disabled:bg-gray-100 disabled:text-gray-400"
                              value={mudasProject}
                              onChange={(e) => setMudasProject(e.target.value)}
                              disabled={!mudasRegion}
                            >
                              <option value="">
                                {mudasRegion ? "Escolha o Projeto..." : "Selecione a Região Primeiro"}
                              </option>
                              {mudasRegion === "sudeste" && (
                                <>
                                  <option value="Reflorestamento Serra do Mar (SP/RJ)">Reflorestamento Serra do Mar (SP/RJ)</option>
                                  <option value="Mata Atlântica Viva (MG)">Mata Atlântica Viva (MG)</option>
                                </>
                              )}
                              {mudasRegion === "norte" && (
                                <>
                                  <option value="Guardiões da Amazônia (AM)">Guardiões da Amazônia (AM)</option>
                                  <option value="Proteção Floresta Amazônica (PA)">Proteção Floresta Amazônica (PA)</option>
                                </>
                              )}
                              {mudasRegion === "nordeste" && (
                                <>
                                  <option value="Recuperação Caatinga (CE/PE)">Recuperação Caatinga (CE/PE)</option>
                                  <option value="Manguezais do Recôncavo (BA)">Manguezais do Recôncavo (BA)</option>
                                </>
                              )}
                              {mudasRegion === "sul" && (
                                <>
                                  <option value="Araucárias do Futuro (PR)">Araucárias do Futuro (PR)</option>
                                  <option value="Mata Atlântica Sul (SC)">Mata Atlântica Sul (SC)</option>
                                </>
                              )}
                              {mudasRegion === "centro-oeste" && (
                                <>
                                  <option value="Nascentes do Pantanal (MT)">Nascentes do Pantanal (MT)</option>
                                  <option value="Conservação do Cerrado (GO)">Conservação do Cerrado (GO)</option>
                                </>
                              )}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Lado B: Social */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Wallet className="h-4 w-4 text-orange-600" />
                        <span className="font-semibold text-sm">Projetos Sociais</span>
                      </div>
                      <div className="flex items-baseline gap-2 mb-3">
                        <span className="text-xl font-bold text-orange-600">R$ {socialValue.toFixed(2).replace(".", ",")}</span>
                        <span className="text-sm font-medium text-orange-600/70">(17,5%)</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="social-region" className="text-xs">Região</Label>
                          <select
                            id="social-region"
                            className="w-full border border-gray-300 rounded-md p-2 bg-white text-gray-900 text-sm h-9"
                            value={socialRegion}
                            onChange={(e) => {
                              setSocialRegion(e.target.value);
                              setSocialProject("");
                            }}
                          >
                            <option value="">Selecione...</option>
                            <option value="sudeste">Sudeste</option>
                            <option value="nordeste">Nordeste</option>
                            <option value="sul">Sul</option>
                            <option value="norte">Norte</option>
                            <option value="centro-oeste">Centro-Oeste</option>
                          </select>
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="social-project" className="text-xs">Causa</Label>
                          <select
                            id="social-project"
                            className="w-full border border-gray-300 rounded-md p-2 bg-white text-gray-900 text-sm h-9 disabled:bg-gray-100 disabled:text-gray-400"
                            value={socialProject}
                            onChange={(e) => setSocialProject(e.target.value)}
                            disabled={!socialRegion}
                          >
                            <option value="">
                              {socialRegion ? "Escolha o Projeto..." : "Selecione a Região Primeiro"}
                            </option>
                            {socialRegion === "sudeste" && (
                              <>
                                <option value="Água para Todos - Cisternas (SP)">Água para Todos - Cisternas (SP)</option>
                                <option value="Educação Rural (RJ)">Educação Rural (RJ)</option>
                                <option value="Saúde Básica (MG)">Saúde Básica (MG)</option>
                              </>
                            )}
                            {socialRegion === "norte" && (
                              <>
                                <option value="Água para Comunidades (AM)">Água para Comunidades (AM)</option>
                                <option value="Educação Indígena (PA)">Educação Indígena (PA)</option>
                                <option value="Saúde na Floresta (AC)">Saúde na Floresta (AC)</option>
                              </>
                            )}
                            {socialRegion === "nordeste" && (
                              <>
                                <option value="Água para Todos - Cisternas (Sertão)">Água para Todos - Cisternas (Sertão)</option>
                                <option value="Educação Rural (Nordeste)">Educação Rural (Nordeste)</option>
                                <option value="Saúde Básica (Nordeste)">Saúde Básica (Nordeste)</option>
                              </>
                            )}
                            {socialRegion === "sul" && (
                              <>
                                <option value="Água Limpa (PR)">Água Limpa (PR)</option>
                                <option value="Educação no Campo (SC)">Educação no Campo (SC)</option>
                                <option value="Saúde Comunitária (RS)">Saúde Comunitária (RS)</option>
                              </>
                            )}
                            {socialRegion === "centro-oeste" && (
                              <>
                                <option value="Água para Todos (MT)">Água para Todos (MT)</option>
                                <option value="Educação Rural (GO)">Educação Rural (GO)</option>
                                <option value="Saúde Básica (MS)">Saúde Básica (MS)</option>
                              </>
                            )}
                          </select>
                        </div>
                      </div>
                    </div>

                    <Button
                      onClick={handleConfirm}
                      disabled={!canConfirm}
                      className="w-full h-12 mt-4 bg-green-600 hover:bg-green-700 text-base font-semibold"
                    >
                      ✅ Confirmar Destinação
                    </Button>
                    <p className="text-xs text-gray-600 text-center mt-2">
                      Ao confirmar, R$ 10,00 serão liberados no seu cartão imediatamente.
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              /* Card de Resumo/Sucesso (Após a Confirmação) */
              <Card className="shadow-lg border-2 border-green-200 bg-white h-full flex flex-col">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Voo SDU-GRU - Compensação Registrada
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 flex-1 flex flex-col justify-between">
                  <div className="space-y-4">
                    {/* Resumo das Destinações (Lista Dinâmica Acumulativa) */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
                      {resumoDestinacoes.length > 0 ? (
                        resumoDestinacoes.map((item) => (
                          <div key={item.id} className="flex items-start gap-3">
                            {/* Ícone condicional baseado no tipo */}
                            <div className="mt-1">
                              {item.tipo === 'mudas' ? (
                                <Sprout className="text-green-600 w-4 h-4" />
                              ) : (
                                <Heart className="text-orange-500 w-4 h-4" />
                              )}
                            </div>
                            
                            <div className="flex-1">
                              <p className="text-sm text-gray-700">
                                Você destinou <span className="font-bold text-gray-900">R$ {item.valor.toFixed(2).replace(".", ",")}</span> para {item.tipo === 'mudas' ? 'Mudas' : 'Projetos Sociais'} ({item.regiao}).
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {item.projeto}
                              </p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-2">
                          Nenhuma destinação registrada ainda.
                        </p>
                      )}
                    </div>

                    {/* Painel de Reinvestimento */}
                    {saldoLivre > 0 && (
                      <div className="bg-green-50 rounded-lg p-4 border border-green-200 space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-gray-900">Seu Saldo Livre: <span className="text-green-600">R$ {saldoLivre.toFixed(2).replace(".", ",")}</span></h3>
                        </div>

                        {/* Opção A: Completar Muda (Se faltar pouco) */}
                        {faltamMudas > 0 && faltamMudas <= saldoLivre && (
                          <Button
                            onClick={handleCompletarMuda}
                            className="w-full bg-green-600 hover:bg-green-700 text-white"
                            size="sm"
                          >
                            <TreePine className="h-4 w-4 mr-2" />
                            Completar 1 muda (R$ {faltamMudas.toFixed(2).replace(".", ",")})
                          </Button>
                        )}

                        {/* Opção B: Doação Livre */}
                        <div className="space-y-3 pt-2 border-t border-green-200">
                          <Label className="text-xs font-semibold text-gray-700">Doação Livre</Label>
                          
                          {/* Linha 1: Valor e Categoria */}
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label htmlFor="valor-doacao" className="text-xs text-gray-600">Valor (R$)</Label>
                              <Input
                                id="valor-doacao"
                                type="text"
                                placeholder="0,00"
                                value={valorDoacao}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/[^\d,]/g, '');
                                  setValorDoacao(value);
                                }}
                                className="h-9 text-sm"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label htmlFor="categoria-doacao" className="text-xs text-gray-600">Categoria</Label>
                              <Select 
                                value={categoriaDoacao} 
                                onValueChange={(value) => {
                                  setCategoriaDoacao(value);
                                  setRegiaoDoacao("");
                                  setProjetoDoacao("");
                                }}
                              >
                                <SelectTrigger id="categoria-doacao" className="h-9 text-sm">
                                  <SelectValue placeholder="Escolha" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Apoio a Mudas">Apoio a Mudas</SelectItem>
                                  <SelectItem value="Projetos Sociais">Projetos Sociais</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {/* Linha 2: Região e Projeto */}
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label htmlFor="regiao-doacao" className="text-xs text-gray-600">Região</Label>
                              <Select 
                                value={regiaoDoacao || undefined} 
                                onValueChange={(value) => {
                                  setRegiaoDoacao(value);
                                  setProjetoDoacao("");
                                }}
                                disabled={!categoriaDoacao}
                              >
                                <SelectTrigger id="regiao-doacao" className="h-9 text-sm">
                                  <SelectValue placeholder={categoriaDoacao ? "Escolha" : "Selecione categoria"} />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="sudeste">Sudeste</SelectItem>
                                  <SelectItem value="nordeste">Nordeste</SelectItem>
                                  <SelectItem value="sul">Sul</SelectItem>
                                  <SelectItem value="norte">Norte</SelectItem>
                                  <SelectItem value="centro-oeste">Centro-Oeste</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label htmlFor="projeto-doacao" className="text-xs text-gray-600">Projeto/Causa</Label>
                              <Select 
                                value={projetoDoacao || undefined} 
                                onValueChange={setProjetoDoacao}
                                disabled={!regiaoDoacao || projetosDisponiveisDoacao.length === 0}
                              >
                                <SelectTrigger id="projeto-doacao" className="h-9 text-sm">
                                  <SelectValue placeholder={regiaoDoacao ? "Escolha" : "Selecione região"} />
                                </SelectTrigger>
                                <SelectContent>
                                  {projetosDisponiveisDoacao.length > 0 ? (
                                    projetosDisponiveisDoacao.map((projeto) => (
                                      <SelectItem key={projeto.id} value={projeto.id}>
                                        {projeto.nome}
                                      </SelectItem>
                                    ))
                                  ) : null}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <Button
                            onClick={handleDoacaoLivre}
                            variant="outline"
                            className="w-full border-green-300 text-green-700 hover:bg-green-100"
                            size="sm"
                            disabled={!valorDoacao || !categoriaDoacao || !regiaoDoacao || !projetoDoacao}
                          >
                            Doar
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Status da Muda (Se houver progresso parcial) */}
                    {totalInvestidoMudas > 0 && mudasProgress < 100 && (
                      <div className="pt-2 border-t border-gray-200">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-gray-500">ℹ️</span>
                          <span className="text-xs text-gray-600">Status da Muda: <span className="font-semibold">{mudasProgress}%</span> completa</span>
                        </div>
                        {faltamMudas > saldoLivre && saldoLivre > 0 && (
                          <p className="text-xs text-gray-500">
                            Falta R$ {faltamMudas.toFixed(2).replace(".", ",")} para completar. Você tem R$ {saldoLivre.toFixed(2).replace(".", ",")} disponível.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

          </div>
        </div>

        {/* Rede de Parceiros - Vitrine de Descontos */}
        <div className="mt-8">
          {/* Cabeçalho da Seção */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">Lojas Parceiras & Benefícios</h2>
            <button
              onClick={() => alert("Redirecionando para página de parceiros...")}
              className="text-green-600 hover:text-green-700 font-medium text-sm flex items-center gap-1 transition-colors"
            >
              Ver todas as lojas
              <span>→</span>
            </button>
          </div>

          {/* Grid de Cards de Lojas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Card 1: Ponto Pet Chic */}
            <Card className="shadow-sm border border-gray-200 rounded-xl hover:shadow-md transition-shadow cursor-pointer">
              <div className="h-32 bg-gray-100 rounded-t-xl flex items-center justify-center">
                <span className="text-4xl">🐶</span>
              </div>
              <CardContent className="p-4">
                <div className="space-y-2">
                  <div>
                    <h3 className="font-semibold text-gray-900">Ponto Pet Chic</h3>
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                      Pet Shop
                    </span>
                  </div>
                  <div className="pt-2 mt-2 border-t border-gray-100">
                    <p className="text-sm font-medium text-green-700 bg-green-50 px-2 py-1 rounded">
                      🐶 10% OFF em rações
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card 2: BioMarket Orgânicos */}
            <Card className="shadow-sm border border-gray-200 rounded-xl hover:shadow-md transition-shadow cursor-pointer">
              <div className="h-32 bg-gray-100 rounded-t-xl flex items-center justify-center">
                <span className="text-4xl">🥦</span>
              </div>
              <CardContent className="p-4">
                <div className="space-y-2">
                  <div>
                    <h3 className="font-semibold text-gray-900">BioMarket Orgânicos</h3>
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                      Alimentação
                    </span>
                  </div>
                  <div className="pt-2 mt-2 border-t border-gray-100">
                    <p className="text-sm font-medium text-green-700 bg-green-50 px-2 py-1 rounded">
                      🥦 Aceita Saldo MSC
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card 3: EcoStyle Moda */}
            <Card className="shadow-sm border border-gray-200 rounded-xl hover:shadow-md transition-shadow cursor-pointer">
              <div className="h-32 bg-gray-100 rounded-t-xl flex items-center justify-center">
                <span className="text-4xl">👕</span>
              </div>
              <CardContent className="p-4">
                <div className="space-y-2">
                  <div>
                    <h3 className="font-semibold text-gray-900">EcoStyle Moda</h3>
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                      Vestuário
                    </span>
                  </div>
                  <div className="pt-2 mt-2 border-t border-gray-100">
                    <p className="text-sm font-medium text-green-700 bg-green-50 px-2 py-1 rounded">
                      👕 R$ 20,00 de desconto
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card 4: Livraria Saber */}
            <Card className="shadow-sm border border-gray-200 rounded-xl hover:shadow-md transition-shadow cursor-pointer">
              <div className="h-32 bg-gray-100 rounded-t-xl flex items-center justify-center">
                <span className="text-4xl">📚</span>
              </div>
              <CardContent className="p-4">
                <div className="space-y-2">
                  <div>
                    <h3 className="font-semibold text-gray-900">Livraria Saber</h3>
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                      Cultura
                    </span>
                  </div>
                  <div className="pt-2 mt-2 border-t border-gray-100">
                    <p className="text-sm font-medium text-green-700 bg-green-50 px-2 py-1 rounded">
                      📚 5% de Cashback extra
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;
